/**
 * @description Выровнять по нижнему правому караю.
 * @type {string}
 */
const SNACKBAR_ALIGN_BOTTOM_RIGHT = 'bottom-right';

/**
 * @description Выровнять по центру.
 * @type {string}
 */
const SNACKBAR_ALIGN_CENTERED = 'centered';

/**
 * @description Базовое время показа.
 * @type {number}
 */
const BASE_TIMEOUT = 3000;

/**
 * @description Длительное время показа.
 * @type {number}
 */
const LONG_TIMEOUT = BASE_TIMEOUT * 3;

/**
 * @description Параметры выравнивания уведомителя по позиции.
 * @type {object}
 */
const SNACKBAR_ALIGN = {
  [SNACKBAR_ALIGN_BOTTOM_RIGHT]: {
    bottom: true,
    centered: false,
    right: true,
  },
  [SNACKBAR_ALIGN_CENTERED]: {
    bottom: false,
    centered: true,
    right: false,
  },
};

/**
 * @description Идентификатор ошибки.
 * @type {string}
 */
const ERROR = 'error';

/**
 * @description Идентификатор неудачи.
 * @type {string}
 */
const FAIL = 'fail';

/**
 * @description Идентификатор успеха.
 * @type {string}
 */
const SUCCESS = 'success';

/**
 * @description Идентификатор высокой важности.
 * @type {string}
 */
const HIGH_IMPORTANCE = 'high-importance';

/**
 * @description Идентификатор обычной важности.
 * @type {string}
 */
const DEFAULT_IMPORTANCE = 'default-importance';

/**
 * @description Идентификатор длительности показа по умолчанию.
 * @type {string}
 */
const DEFAULT_DURATION = 'default-duration';

/**
 * @description Идентификатор большой длительности.
 * @type {string}
 */
const LONG_DURATION = 'long-duration';

/**
 * @description Отобразить уровень уведомления на цвет.
 * @param {string} level Уровень.
 * @returns {string}
 */
const mapLevelOnColor = (level) => ({
  [ERROR]: 'red',
  [FAIL]: 'orange',
  [SUCCESS]: 'green',
})[level] ?? 'gray';

/**
 * @description Отобразить важность уведомления на выравнивание.
 * @param {string} importance Важность.
 * @returns {string}
 */
const mapImportanceOnAlign = (importance) => ({
  [HIGH_IMPORTANCE]: SNACKBAR_ALIGN_CENTERED,
  [DEFAULT_IMPORTANCE]: SNACKBAR_ALIGN_BOTTOM_RIGHT,
})[importance] ?? SNACKBAR_ALIGN_BOTTOM_RIGHT;

/**
 * @description Отобразить длительность показа в миллисекунды.
 * @param {string} duration Идентификатор длительности.
 * @returns {number}
 */
const mapTimeoutByDuration = (duration) => ({
  [LONG_DURATION]: LONG_TIMEOUT,
  [DEFAULT_DURATION]: BASE_TIMEOUT,
})[duration] ?? BASE_TIMEOUT;

/**
 * @description Создать неблокирующее уведомление.
 * @param {number} duration Продолжительность показа.
 * @returns {function}
 */
const createSnackbarNotify = (duration) => {
  const timeout = mapTimeoutByDuration(duration);
  return (importance) => {
    const align = mapImportanceOnAlign(importance);
    return (level) => {
      const color = mapLevelOnColor(level);
      return function _(message) {
        this.showNotificationSnackbar(message, color, align, timeout);
      };
    };
  };
};

/**
 * @description Примесь размещения диалогового уведомителя.
 */
export const notifierDialogSiteMixin = {
  data() {
    return {
      /**
       * @description Состояние диалога уведомления.
       * @type {boolean}
       */
      notificationDialog: false,
      /**
       * @description Текст диалога уведомления.
       * @type {string}
       */
      notificationDialogText: '',
      /**
       * @description Телеграмм ссылка.
       * @type {string}
       */
      telegramLink: null,
      /**
       * @description Ватсапп ссылка.
       * @type {string}
       */
      whatsAppLink: null,
      /**
       * @description Заголовок диалога уведомления.
       * @type {string}
       */
      notificationDialogTitle: '',
    };
  },
  methods: {
    /**
     * @description Обработать ввод диалога уведомления.
     * @param {boolean} updatedValue Значение диалога.
     * @returns {void}
     */
    onInputNotificationDialog(updatedValue) {
      this.notificationDialog = updatedValue;
      if (updatedValue !== true) {
        this.notificationDialogText = '';
        this.notificationDialogTitle = '';
      }
    },
  },
  provide() {
    return {
      /**
       * @description Показать диалог уведомления.
       * @param {string} title Заголовок.
       * @param {string} text  Содержимое.
       * @param {string} telegramLink  Телеграм ссылка.
       * @param {string} whatsAppLink  Ватсапп ссылка.
       * @returns {void}
       */
      showNotificationModalDialog: (title, text = '', telegramLink = null, whatsAppLink = null) => {
        this.notificationDialog = true;
        this.notificationDialogText = text;
        this.notificationDialogTitle = title;
        this.telegramLink = telegramLink;
        this.whatsAppLink = whatsAppLink;
      },
    };
  },
};

/**
 * @description Примесь размещения неблокирующего уведомителя.
 */
export const notifierSnackbarSiteMixin = {
  data() {
    return {
      /**
       * @description Состояние уведомления.
       * @type {boolean}
       */
      notificationSnackbar: false,
      /**
       * @description Параметры позиции уведомления.
       * @type {object}
       */
      notificationSnackbarAlign: SNACKBAR_ALIGN[SNACKBAR_ALIGN_BOTTOM_RIGHT],
      /**
       * @description Параметры цвета уведомления.
       * @type {object}
       */
      notificationSnackbarColor: 'green darken-2',
      /**
       * @description Содержимое уведомления.
       * @type {object}
       */
      notificationSnackbarText: '',
      /**
       * @description Время показа уведомления.
       * @type {object}
       */
      notificationSnackbarTimeout: BASE_TIMEOUT,
    };
  },
  methods: {
    /**
     * @description Обработать закрытие неблокирующего уведомления.
     * @returns {void}
     */
    onCloseNotificationSnackbar() {
      this.notificationSnackbar = false;
    },
  },
  provide() {
    return {
      showNotificationSnackbar: (message, color, align, timeout) => {
        this.notificationSnackbarTimeout = 0;
        this.$nextTick(() => {
          this.notificationSnackbarAlign = SNACKBAR_ALIGN[align]
            || SNACKBAR_ALIGN[mapImportanceOnAlign(DEFAULT_IMPORTANCE)];
          this.notificationSnackbarColor = color;
          this.notificationSnackbarText = message;
          this.notificationSnackbarTimeout = timeout;
          this.notificationSnackbar = true;
        });
      },
    };
  },
};

/**
 * @description Примесь отправителя уведомлений.
 */
export default {
  inject: [
    'showNotificationModalDialog',
    'showNotificationSnackbar',
  ],
  methods: {
    /**
     * @description Уточнить взаимодействия уведомления о неудаче.
     * @param {string} interaction Взаимодействие.
     * @returns {function(string): void}
     */
    clarifyAndNotifyFail(interaction) {
      if (interaction === 'blocking') {
        return this.showNotificationModalDialog;
      }
      return this.notifyFail;
    },
    /**
     * @description Уведомить об ошибке.
     * @param {string} message Сообщение.
     * @returns {void}
     */
    notifyError: createSnackbarNotify(DEFAULT_DURATION)(DEFAULT_IMPORTANCE)(ERROR),
    /**
     * @description Уведомить о важной неудаче.
     * @param {string} message Сообщение.
     * @returns {void}
     */
    notifyImportanceFail: createSnackbarNotify(DEFAULT_DURATION)(HIGH_IMPORTANCE)(FAIL),
    /**
     * @description Уведомить о неудаче.
     * @param {string} message Сообщение.
     * @returns {void}
     */
    notifyFail: createSnackbarNotify(DEFAULT_DURATION)(DEFAULT_IMPORTANCE)(FAIL),
    /**
     * @description Уведомить об успехе.
     * @param {string} message Сообщение.
     * @returns {void}
     */
    notifySuccess: createSnackbarNotify(DEFAULT_DURATION)(DEFAULT_IMPORTANCE)(SUCCESS),
    /**
     * @description Уведомить об успехе длительно.
     * @param {string} message Сообщение.
     * @returns {void}
     */
    notifySuccessLong: createSnackbarNotify(LONG_DURATION)(DEFAULT_IMPORTANCE)(SUCCESS),
  },
};
